import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Scroll, CheckCircle2, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicContract() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/contract/${token}`);
      setContract(r.data);
      if (r.data.status === "signed") setSigned(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Contract not found.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [token]);

  const sign = async () => {
    if (!typedName.trim() || typedName.trim().length < 2) {
      toast.error("Please type your full name to sign.");
      return;
    }
    setSigning(true);
    try {
      await axios.post(`${API}/public/contract/${token}/sign`, { typed_name: typedName.trim() });
      setSigned(true);
      await load();
      toast.success("Signed. Thank you.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not sign contract.");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center" style={{ color: "var(--ink-soft)" }}>
        <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading contract…
      </div>
    );
  }
  if (error || !contract) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center">
        <AlertTriangle className="h-10 w-10 inline mb-3" style={{ color: "var(--terracotta)" }} />
        <h1 className="font-serif text-3xl">Contract unavailable</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>{error}</p>
      </div>
    );
  }

  const vendor = contract.parties?.vendor || {};
  const client = contract.parties?.client || {};

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10" data-testid="public-contract-page">
      {signed && (
        <div
          data-testid="public-contract-signed-banner"
          className="rounded-sm p-4 mb-6 flex items-center gap-3"
          style={{ background: "rgba(31,93,52,0.1)", borderLeft: "3px solid var(--moss)" }}
        >
          <CheckCircle2 className="h-5 w-5" style={{ color: "var(--moss)" }} />
          <div>
            <div className="font-semibold">
              Signed by {contract.signature?.typed_name}
            </div>
            <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
              {contract.signed_at}
            </div>
          </div>
        </div>
      )}

      <div className="card-warm rounded-sm p-6 sm:p-8">
        <div className="overline" style={{ color: "var(--terracotta)" }}>
          <Scroll className="h-3 w-3 inline mr-1" /> Contract {contract.number}
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl mt-2">{contract.title}</h1>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-sm p-3" style={{ background: "var(--sand)" }}>
            <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--ink-soft)" }}>Vendor</div>
            <div className="font-semibold">{vendor.name}</div>
            {vendor.email && <div className="text-xs" style={{ color: "var(--ink-soft)" }}>{vendor.email}</div>}
          </div>
          <div className="rounded-sm p-3" style={{ background: "var(--sand)" }}>
            <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--ink-soft)" }}>Client</div>
            <div className="font-semibold">{client.name}</div>
            {client.email && <div className="text-xs" style={{ color: "var(--ink-soft)" }}>{client.email}</div>}
          </div>
        </div>

        {contract.intro && (
          <p className="mt-6 text-sm" style={{ color: "var(--ink-soft)", whiteSpace: "pre-wrap" }}>
            {contract.intro}
          </p>
        )}

        <div className="mt-6 space-y-5" data-testid="public-contract-sections">
          {(contract.sections || []).map((s, idx) => (
            <section key={idx}>
              {s.heading && (
                <h3 className="font-semibold mb-1.5" style={{ color: "var(--terracotta)" }}>
                  {s.heading}
                </h3>
              )}
              {s.body && (
                <p className="text-sm" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                  {s.body}
                </p>
              )}
            </section>
          ))}
        </div>

        {contract.notes && (
          <div className="mt-6 rounded-sm p-4 text-sm" style={{ background: "var(--sand)" }}>
            <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--ink-soft)" }}>Additional notes</div>
            {contract.notes}
          </div>
        )}

        {!signed ? (
          <div className="mt-8 rounded-sm border p-5" style={{ borderColor: "var(--sand)" }}>
            <h3 className="font-semibold mb-2">Sign this contract</h3>
            <p className="text-xs mb-3" style={{ color: "var(--ink-soft)" }}>
              Type your full name below as your e-signature. By signing, you agree to the terms above.
              Your signature is recorded along with the date, time, and your IP address.
            </p>
            <div className="flex items-stretch gap-2 flex-wrap">
              <Input
                data-testid="public-contract-typed-name"
                className="flex-1 min-w-[200px]"
                placeholder="Type your full name"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
              />
              <Button
                data-testid="public-contract-sign-btn"
                className="btn-primary rounded-full px-6 inline-flex items-center gap-2"
                onClick={sign}
                disabled={signing || !typedName.trim()}
              >
                {signing && <Loader2 className="h-4 w-4 animate-spin" />}
                Sign contract
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-8 text-center">
            <a
              data-testid="public-contract-pdf"
              href={`${API}/public/contract/${token}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm inline-flex items-center gap-1 underline"
              style={{ color: "var(--ink-soft)" }}
            >
              <Download className="h-4 w-4" /> Download signed PDF
            </a>
          </div>
        )}

        <div className="mt-6 text-xs italic" style={{ color: "var(--ink-soft)" }}>
          This is a template contract generated for convenience. It is not legal advice —
          where appropriate, both parties should have their own counsel review.
        </div>
      </div>

      <div className="text-center mt-6 text-xs" style={{ color: "var(--ink-soft)" }}>
        Powered by <span style={{ color: "var(--terracotta)" }}>AddVendTour</span>
      </div>
    </div>
  );
}
